import { useCallback, useEffect, useState } from 'react'
import type { Diamond } from '../api/types'
import { favoriteStonesApi } from '../api/endpoints'
import {
  isFavoriteStone,
  loadFavoriteStones,
  setFavoriteStone,
  subscribeFavoriteStones,
} from '../lib/sessionCache'
import { feedback } from '../lib/feedback'
import { toast } from '../lib/toast'
import './stoneSpecs.css'

/**
 * STONE ROW ACTIONS — the shared vocabulary for "who lists this stone", "save
 * it", and "can I buy it here", used by the desktop results table, the mobile
 * browse row and the mobile card. One implementation so the two shells cannot
 * drift on any of the three, because all three are GATED and a gate that is
 * expressed twice is a gate that will eventually disagree with itself.
 */

/* ════════════════════════════════════════════════════════════════════════
   1. THE SUPPLIER, AND WHY IT IS SO OFTEN NOT THERE
   ════════════════════════════════════════════════════════════════════════

   CLAUDE.md invariant 3: prices, refStockId AND SUPPLIER CONTACT are revealed
   only to a caller who is signed in AND KYC-approved. `projectStone` populates
   the `supplier` block (and `supplierId`) for that caller and nulls both for
   everyone else — deliberately, and it is the same allowlist that keeps the
   cost book private.

   So the ONLY honest client-side signal for "may this viewer see the supplier"
   is: did the server send a supplier name? Nothing else in the payload may be
   used to reconstruct it. `supplierId` is gated by the same rule, the supplier
   facet is gated, and the media URLs are sealed through /api/v precisely so the
   originating host cannot be read off a thumbnail. There is no second source,
   and inventing one would re-open the leak the projection exists to close.

   What shipped before this module: the desktop table hid the whole Supplier
   COLUMN for an anonymous viewer and printed a bare em-dash for a signed-in-
   but-unverified one. The mobile row and card had no supplier at all. An
   em-dash reads as "this stone has no supplier" — it is a lie about the data,
   and it wastes the single strongest argument for finishing verification.
   Hence a real locked state that states what verification BUYS.                */

export type SupplierRevealState = 'revealed' | 'signed-out' | 'unverified'

export interface SupplierReveal {
  state: SupplierRevealState
  /** The real display name, and only ever the one the server sent. */
  name: string | null
}

/**
 * Resolve what this viewer may see of the listing supplier.
 *
 * `signedOut` is the caller's own knowledge of the session (the desktop shell
 * threads it as `blurPrice`; the mobile shell is auth-first and never renders
 * browse signed-out) and is used ONLY to pick which locked message to show.
 * It can never turn a locked cell into a revealed one — that is decided purely
 * by whether the payload carried a name.
 */
export function supplierReveal(stone: Diamond, signedOut = false): SupplierReveal {
  const name = stone.supplier?.name?.trim() || null
  if (name) return { state: 'revealed', name }
  return { state: signedOut ? 'signed-out' : 'unverified', name: null }
}

/**
 * The locked copy. Two audiences, two different next actions — and the long
 * sentence is what a screen reader and a tooltip get in BOTH cases, because
 * "Verified buyers" on its own is a fragment, not an explanation.
 */
const LOCKED_COPY: Record<Exclude<SupplierRevealState, 'revealed'>, {
  short: string
  long: string
  sentence: string
}> = {
  'signed-out': {
    short: 'Sign in to see',
    long: 'Sign in to see the supplier',
    sentence:
      'Sign in and get verified to see who lists this stone — and contact them directly, with no Diaex fee.',
  },
  unverified: {
    short: 'Verified buyers',
    long: 'Verified buyers see the supplier',
    sentence:
      'Verified buyers see who lists this stone and can contact them directly, with no Diaex fee. Verification takes 1–2 business days.',
  },
}

function IconLock({ size = 10 }: { size?: number }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden
    >
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  )
}

/**
 * The supplier cell / line. Revealed → the name. Locked → a lock glyph plus a
 * short label, with the full value proposition as the accessible name and the
 * tooltip, so the abbreviation is never the only thing said.
 *
 * `compact` is the dense-table register ("Verified buyers"); the roomy register
 * ("Verified buyers see the supplier") is for cards, which have the width.
 */
export function SupplierName({
  stone,
  signedOut = false,
  compact = false,
  className = '',
}: {
  stone: Diamond
  signedOut?: boolean
  compact?: boolean
  className?: string
}) {
  const { state, name } = supplierReveal(stone, signedOut)

  if (state === 'revealed') {
    return (
      <span
        className={`supplier-name ${className}`}
        title={name ?? undefined}
        data-testid="stone-supplier"
        data-state="revealed"
      >
        <span className="sr-only">Supplier: </span>
        {name}
      </span>
    )
  }

  const copy = LOCKED_COPY[state]
  return (
    <span
      className={`supplier-locked ${className}`}
      title={copy.sentence}
      data-testid="stone-supplier"
      data-state={state}
    >
      <IconLock />
      {/* The visible label is a fragment; the sentence beside it is what is
          actually announced, so a screen-reader user hears the reason and the
          payoff rather than two words of chrome. */}
      <span aria-hidden="true">{compact ? copy.short : copy.long}</span>
      <span className="sr-only">{copy.sentence}</span>
    </span>
  )
}

/* ════════════════════════════════════════════════════════════════════════
   2. SAVE STONE — the EXISTING favourite mechanism, not a second one
   ════════════════════════════════════════════════════════════════════════

   There are two distinct favourites in this product and they must not be
   conflated (MediaModal.tsx says so where they sit side by side):

     · favourite SUPPLIER  — a HEART, saves a contact  (/me/favorite-suppliers)
     · save STONE          — a BOOKMARK, saves a stone (/me/favorite-stones)

   Tej asked for "a favorite button" on the browse row; the stone is the thing
   in the row, so this is the stone bookmark — same endpoint, same shared
   session cache, same optimistic-then-corrected write as the detail surfaces,
   and therefore the same shape so the two never read as different actions.
   `lib/sessionCache.ts` de-duplicates the list fetch across every mounted
   button (one request per session, not one per row) and notifies all
   subscribers on a toggle, so saving from a row repaints the detail screen's
   button and /favorites at the same instant.                                   */

export function SaveStoneButton({
  stone,
  signedOut = false,
  onRequireSignIn,
  className = '',
  size = 13,
  testId = 'stone-save',
}: {
  stone: Diamond
  /** Anonymous viewer — the toggle would 401, so the press routes to sign-in. */
  signedOut?: boolean
  /** Where an anonymous press should GO. Without it the press still says
   *  something (a toast) — a silent no-op is never acceptable. */
  onRequireSignIn?: () => void
  className?: string
  size?: number
  testId?: string
}) {
  const stockId = stone.stockId
  const [saved, setSaved] = useState<boolean>(() => isFavoriteStone(stockId) ?? false)

  useEffect(() => {
    if (signedOut) return
    const sync = () => setSaved(isFavoriteStone(stockId) ?? false)
    const unsub = subscribeFavoriteStones(sync)
    void loadFavoriteStones().then(sync)
    return unsub
  }, [stockId, signedOut])

  const toggle = useCallback(async () => {
    if (signedOut) {
      // A real way forward, never a dead press. The handler is the good path;
      // the toast is the floor when a surface has no router to hand.
      feedback.tap()
      if (onRequireSignIn) onRequireSignIn()
      else toast('Sign in to save stones to your list.', 'info')
      return
    }
    const next = !saved
    feedback.toggle(next)
    setFavoriteStone(stockId, next) // optimistic — repaints every mounted button
    try {
      const r = await favoriteStonesApi.toggle(stockId)
      setFavoriteStone(stockId, r.favorited) // server is the truth
    } catch {
      setFavoriteStone(stockId, !next) // revert
      toast('Could not update your saved stones — try again.', 'error')
    }
  }, [signedOut, onRequireSignIn, saved, stockId])

  const label = signedOut
    ? `Sign in to save ${stone.stockId}`
    : saved
      ? `Remove ${stone.stockId} from saved stones`
      : `Save ${stone.stockId} to your stones`

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); void toggle() }}
      // Stops the mobile row's tap-and-hold timer from arming under a press
      // that is not a row press.
      onPointerDown={(e) => e.stopPropagation()}
      aria-pressed={signedOut ? undefined : saved}
      aria-label={label}
      title={label}
      data-testid={testId}
      // `data-active` keeps a SAVED bookmark at full opacity while an unsaved
      // one stays recessive — state is information, an affordance is not.
      data-active={saved || undefined}
      className={`row-action tap-44 inline-flex items-center justify-center rounded-full transition-colors duration-micro ${
        saved
          ? 'text-gold-500 hover:text-gold-600'
          : 'text-navy-300 hover:bg-navy-100/40 hover:text-ink'
      } ${className}`}
    >
      <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill={saved ? 'currentColor' : 'none'} stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden
      >
        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
      </svg>
    </button>
  )
}
